<template lang="pug">
.p-product-list__item(v-if='isLoading', :style="itemPlaceholderStyle")
  .p-product-list__item-placeholder.c-loading
    .p-product-list__item-placeholder-inner
.p-product-list__item(
  v-else,
  :class='itemClass',
  :data-rank='config.showRank ? config.rank : ""'
)
  a.p-product-list__wrap-link(v-if='config.showWrapLink', :href='product.url', @click='onClick', :target='target')
  .p-product-list__item-inner(v-show='!isLoading')
    a.p-product-list__thumb(:href='product.url', @click='onClick', :title='product.title', :target='target')
      img.nodraggable(v-if='useVueLazyload', :src='dummyThumbnail', v-lazy='{src: productThumbnailSrc, error: dummyThumbnail, loading: dummyThumbnail}', :data-srcset='productThumbnailSrcSet', :width='config.thumbnailSize', :height='config.thumbnailSize', :alt='product.title')
      img.nodraggable(v-else, :src='dummyThumbnail', :data-lazy='productThumbnailSrc', :data-srcset='productThumbnailSrcSet', :width='config.thumbnailSize', :height='config.thumbnailSize', :alt='product.title')
    .p-product-list__action(v-if='config.showAction')
      button.c-btn.c-btn--link-opacity.p-product-list__add-to-wishlist(
      :title='wishListTooltipLabel',
      :class='wishListButtonClass',
      @click='onAddToWishList'
      )
        i.icon-heart

    .p-product-list__body(v-if='config.showBody', :class="{ 'p-product-list__body--no-action': !config.showAction }")
      a.p-product-list__title(:href='product.url', @click='onClick', :title='product.title', :target='config.target', v-text='product.title')
      .p-price.p-product-list__price(v-if='config.showPrice && !!sellingPriceObject')
        span.p-price__regular(v-html='displayRegularPrice', v-if='displayRegularPrice')
        vue-price-emphasis(:price-object="sellingPriceObject")

        span.p-price__discount-per(v-text='displayDiscountRate', v-if='displayDiscountRate')
        .p-point(v-if='isShowPoints')
          .p-point__earn-point(:class="pointClasses")
            //- ポイント率が1つ以上なら大きい方を示す
            span(v-if='product.points.min < product.points.max', v-text="__('Up to') + ' '")
            i.icon-tom-point
            span.strong(v-text="addFigure(product.points.max, 2)")
            span(v-text="' ' + __('cash back')")

      .p-product-list__labels(v-if='config.showLabel')
        template(v-if='product.is_out_of_stock')
          span.c-label.c-label--sm.c-label--round.c-label--soldout(v-if='product.is_discontinued', v-text="__('Sold out')")
          span.c-label.c-label--sm.c-label--round.c-label--outofstock(v-else, v-text="__('Out of Stock')")
        template(v-else)
          //- デジタルコンテンツのとき
          span.c-label.c-label--sm.c-label--round.c-label--info(v-if='product.is_digital_content', v-text="__('Download')")

          //- 予約商品 or BTO
          span.c-label.c-label--sm.c-label--round.c-label--info(v-if='product.sales_method === "pre_order"', v-text="__('Pre-order')")
          span.c-label.c-label--sm.c-label--round.c-label--info(v-if='product.sales_method === "bto" && !product.is_serial_code', v-text="__('Special Order')")

      .p-product-list__reviews(v-if='config.showReview && product.review_stats.count > 0')
        .c-rating.c-rating--sm(:data-rating='Math.round(product.review_stats.average_stars * 2) / 2')
          .c-rating__stars
          a.c-rating__count(
            :href='productReviewsURL',
            @click='onClick',
            :title='product.title',
            :target='target',
            v-text='"(" + product.review_stats.count + ")"'
          )
      slot(:product='product')
</template>

<script>
import { NO_POINT_SKU_NAMES } from '../../../constants/products';
import productItemOptionsMixin from './productItemOptionsMixin';
import { default as urlUtil } from '../../../util/url';
import { default as textUtil } from '../../../util/text';
import { default as arrayUtil } from '../../../util/array';
import { default as i18nUtil } from '../../../util/i18n';
import { Currency } from '../../../util/mixin/currency.mixin';

import priceEmphasisComponent from '../priceEmphasis.vue';

const statuses = {
  INIT: 'init',
  ACTIVATED: 'activated'
};

export default {
  components: {
    'vue-price-emphasis': priceEmphasisComponent
  },

  props: {
    // Product ID
    productId: {
      type: String,
      required: true
    },

    useVueLazyload: {
      type: Boolean
    }
  },

  mixins: [productItemOptionsMixin, Currency],

  data() {
    return {
      status: statuses.INIT,
      config: {},
      isUpdatedWishList: false
    };
  },

  created() {
    this.wishlistModalBus =
      window.vueEventBus != null ? window.vueEventBus.wishlistModal : undefined;
    if (this.wishlistModalBus) {
      this.wishlistModalBus.$on('closeWishlistModal', (res) => {
        if (res.productId === this.productId) {
          this.isUpdatedWishList =
            this.product.is_added_to_wishlist !== res.isInWishlist;
          return this.$store.dispatch('searchProducts/addToWishlist', {
            product_id: this.productId,
            is_added_to_wishlist: res.isInWishlist
          });
        }
      });
    }

    this.config = {
      target: this.target,
      itemClasses: arrayUtil.arrayToClassObject(
        textUtil.stringToArray(this.itemClasses, /\s+|,\s*/)
      ),
      thumbnailSize: parseInt(this.thumbnailSize, 10),

      showWrapLink: textUtil.toBoolean(this.showWrapLink),
      showBody: textUtil.toBoolean(this.showBody),
      showPrice: textUtil.toBoolean(this.showPrice),
      showAction: textUtil.toBoolean(this.showAction),
      showLabel: textUtil.toBoolean(this.showLabel),
      showReview: textUtil.toBoolean(this.showReview),
      showRank: textUtil.toBoolean(this.showRank),
      rank: parseInt(this.rank, 10),

      impressionList: this.impressionList,
      impressionDimension1: this.impressionDimension1,

      analyticsCategory: this.analyticsCategory,
      analyticsAction: this.analyticsAction,
      analyticsLabel: this.analyticsLabel,

      trackingCategory: this.trackingCategory,
      trackingAction: this.trackingAction,
      trackingLabel: this.trackingLabel,
      trackingOption1: this.trackingOption1,
      trackingOption2: this.trackingOption2
    };
  },

  /*
    ライフイベントで status を変更する
    watch しているのはスライダー時表示のときダミー値でプレースホルダーを生成しているため
    */
  watch: {
    productId() {
      this.checkActivate();
    }
  },

  mounted() {
    this.checkActivate();
  },

  computed: {
    // ローディング中かどうか
    isLoading() {
      return this.status !== statuses.ACTIVATED;
    },

    // 自身に対応した商品データを商品ストアから参照する
    product() {
      if (this.isLoading) {
        return {};
      }
      return this.$store.getters['searchProducts/findById'](this.productId);
    },

    // 複数SKUを保持しているか
    hasMultiSku() {
      return this.product.skus.length > 1;
    },

    // 商品アイテムコンテナに使用するクラスオブジェクトを返す
    itemClass() {
      return Object.assign(this.config.itemClasses, {
        'p-product-list__item--new':
          !this.product.is_out_of_stock && this.product.new_arrival,
        'p-product-list__item--exclusive-product':
          this.product.is_exclusive_product,
        'p-product-list__item--rank': this.config.showRank
      });
    },

    // Favoriteボタンに適用するクラスオブジェクトを返す
    wishListButtonClass() {
      const classes = {
        added:
          this.product != null ? this.product.is_added_to_wishlist : undefined,
        removed: !(this.product != null
          ? this.product.is_added_to_wishlist
          : undefined),
        in: this.isUpdatedWishList
      };
      return classes;
    },

    // Favoriteボタンに適用するツールチップラベルを返す
    wishListTooltipLabel() {
      if (this.product.is_added_to_wishlist) {
        return 'Added to Favorites';
      }
      return 'Add to Favorites';
    },

    isShowPoints() {
      if (this.product.is_points_redemption) {
        return false;
      }
      if (NO_POINT_SKU_NAMES.indexOf(this.product.title) > -1) {
        return false;
      }
      return (
        (this.product.points.min > 0 || this.product.points.max > 0) &&
        (this.product.points.min > 40 || this.product.points.max > 40)
      );
    },

    pointBackPercentage() {
      let price = 0;
      if (this.product.prices.discounted) {
        if (this.product.prices.min_selling < this.product.prices.max_selling) {
          price = this.product.prices.max_selling;
        } else {
          price = this.product.prices.min_selling;
        }
      } else {
        if (this.product.prices.min < this.product.prices.max) {
          price = this.product.prices.max;
        } else {
          price = this.product.prices.min;
        }
      }
      if (price > 0) {
        return (Number(this.product.points.max) / Number(price)) * 100;
      }
      return 0;
    },

    // ポイント率に応じたクラスを返す
    pointClasses() {
      const pointRate = Number(this.pointBackPercentage);
      if (pointRate >= 20) {
        return 'u-color--point-lv3';
      } else if (pointRate >= 10) {
        return 'u-color--point-lv2';
      } else if (pointRate > 1) {
        return 'u-color--point-lv1';
      } else {
        return 'u-color--point-regular';
      }
    },

    // 通常価格を表示する文字列を返す（セール中でなければ0）
    displayRegularPrice() {
      if (!this.product.prices.discounted) {
        return 0;
      }
      let displayPrice;
      if (this.product.prices.min < this.product.prices.max) {
        displayPrice = `${textUtil.currency(
          this.product.prices.min
        )} - ${textUtil.currency(this.product.prices.max)}`;
      } else {
        displayPrice = textUtil.currency(this.product.prices.min);
      }
      if (this.product.is_points_redemption) {
        displayPrice = displayPrice.replace(
          /\$/g,
          '<i class="icon-tom-point"></i>'
        );
      }
      return displayPrice;
    },

    // 購入価格を表示する文字列オブジェクトを返す
    sellingPriceObject() {
      const discounted = this.product.prices.discounted;
      const min = discounted
        ? this.product.prices.min_selling
        : this.product.prices.min;
      if (min == null) {
        // 価格が取得できない場合はnullを返して価格を表示しない
        return null;
      }
      const max = discounted
        ? this.product.prices.min_selling < this.product.prices.max_selling
          ? this.product.prices.max_selling
          : null
        : this.product.prices.min < this.product.prices.max
          ? this.product.prices.max
          : null;
      const decimal = '.';
      const minArr = min.toFixed(2).split(decimal);
      const maxArr = (max && max.toFixed(2).split(decimal)) || null;
      return {
        surface: max
          ? `${textUtil.currency(min)} - ${textUtil.currency(max)}`
          : textUtil.currency(min),
        symbol: this.product.is_points_redemption
          ? '<i class="icon-tom-point"></i>'
          : '$',
        decimal,
        min: {
          whole: Intl.NumberFormat().format(minArr[0]).toString(),
          fraction: minArr[1]
        },
        max: max
          ? {
              whole: Intl.NumberFormat().format(maxArr[0]).toString(),
              fraction: maxArr[1]
            }
          : null
      };
    },

    // 割引率を表示する文字列を返す
    displayDiscountRate() {
      if (!this.discountRate) {
        return 0;
      }
      if (this.product.has_different_discount_rate) {
        return `Up to ${this.discountRate}% OFF`;
      } else {
        return `${this.discountRate}% OFF`;
      }
    },

    // 割引率を返す
    discountRate() {
      if (!this.product.prices.discounted) {
        return 0;
      }
      return Math.round(this.product.prices.discount_rate * 100);
    },

    // 子SKUのボーナスポイントの差があるかどうか
    hasMultiplePointRates() {
      return this.product.points.min < this.product.points.max;
    },

    productReviewsURL() {
      if (this.$store.state.user.is_smart_phone_device) {
        return `/shop/reviews/${this.product._id}`;
      }
      return `${this.product.url}#customerReviews`;
    },

    // GA 拡張 EC に送信する impression オブジェクトを生成して返す
    impression() {
      const impression = {
        id: this.product.skus[0]._id,
        name: this.product.title,
        productId: this.product._id
      };
      if (
        this.product.category != null ? this.product.category.length : undefined
      ) {
        impression.category = this.product.category.join('/');
      }
      if (this.config.impressionList) {
        impression.list = this.config.impressionList;
      }
      if (this.config.impressionDimension1) {
        impression.dimension1 = this.config.impressionDimension1;
      }
      return impression;
    },

    // GA イベントに送信するオブジェクトを生成して返す
    analytics() {
      const analytics = {
        category: this.config.analyticsCategory,
        action: this.config.analyticsAction,
        label: this.config.analyticsLabel.replace(/%s/g, this.product._id)
      };
      return analytics;
    },

    // サムネイル画像パスを生成して返す
    productThumbnailSrc() {
      return urlUtil.webp(
        urlUtil.resizedImageUrl(
          this.product.main_image || this.product.images[0],
          this.config.thumbnailSize,
          this.config.thumbnailSize
        )
      );
    },

    // サムネイル画像 srcSet に使用するパスを生成して返す
    productThumbnailSrcSet() {
      if (!this.$store.state.user.is_smart_phone_device) {
        return false;
      }
      const src2x = urlUtil.webp(
        urlUtil.resizedImageUrl(
          this.product.main_image || this.product.images[0],
          this.config.thumbnailSize,
          this.config.thumbnailSize
        )
      );
      return `${this.productThumbnailSrc} 1x, ${src2x} 2x`;
    },

    dummyThumbnail() {
      return 'https://dzt1km7tv28ex.cloudfront.net/static/shop/product_no_image.png';
    }
  },

  methods: {
    /*
      Product idが存在しアクティブになったかどうかをチェックし status を変更する
      */
    checkActivate() {
      if (this.productId) {
        this.status = statuses.ACTIVATED;
      }
    },

    /*
      商品へのリンクをクリックしたときに呼び出される
      User event
      */
    onClick() {
      tom.helper.ga.list.click(
        this.impression,
        this.config.impressionList,
        this.analytics
      );
      this.$emit('click', this.impression);
    },

    /*
      商品をFavoritesに入れようとしたときに呼び出される
      User event
      */
    onAddToWishList(e) {
      e.stopPropagation();
      this.addToWishList();
    },

    /*
      商品SKUをFavoritesに入れる
      */
    addToWishList() {
      if (!this.$store.state.user.is_login) {
        window.location.href = `/login?r=${encodeURIComponent(
          window.location.href
        )}&via=wishlist`;
        return;
      }

      if (this.wishlistModalBus) {
        this.wishlistModalBus.$emit('addToWishlists', {
          productId: this.productId,
          skuId: this.product.skus[0]._id
        });
      }

      tom.helper.ga.push([
        'event',
        'EC',
        'Add to Wish List from List',
        this.product.skus[0]._id,
        1
      ]);
    },

    __: i18nUtil.__,
    staticImageUrl: urlUtil.staticImageUrl
  }
};
</script>
